import { useState, useCallback, useMemo } from "react";

const STARRED_NOTEBOOK_IDS_KEY = "tars-starred-notebook-ids";

function loadStarredIds(): Set<number> {
    try {
        const raw = localStorage.getItem(STARRED_NOTEBOOK_IDS_KEY);
        if (!raw) return new Set();
        const arr = JSON.parse(raw) as number[];
        return Array.isArray(arr) ? new Set(arr) : new Set();
    } catch {
        return new Set();
    }
}

function saveStarredIds(ids: Set<number>) {
    try {
        localStorage.setItem(STARRED_NOTEBOOK_IDS_KEY, JSON.stringify([...ids]));
    } catch {
        // ignore
    }
}

export function useStarredNotebooks() {
    const [starredIds, setStarredIds] = useState<Set<number>>(loadStarredIds);

    const toggleStarred = useCallback((notebookId: number) => {
        setStarredIds((prev) => {
            const next = new Set(prev);
            if (next.has(notebookId)) {
                next.delete(notebookId);
            } else {
                next.add(notebookId);
            }
            saveStarredIds(next);
            return next;
        });
    }, []);

    const isStarred = useCallback(
        (notebookId: number) => starredIds.has(notebookId),
        [starredIds]
    );

    return useMemo(
        () => ({ starredIds, toggleStarred, isStarred }),
        [starredIds, toggleStarred, isStarred]
    );
}
